/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';

import { REPORT_COLOR_SELECTION_MASTER, ReportService } from '@gh/builder-shared';
import { CostCenterRestService, CsMasterRestService, JobRestService, reportIdField } from '@gh/builder-shared-data';
import { formStateSnapshot, getRouteParam, getRouteQueryParam, isRouteForCreationMode } from '@gh/common-shared';
import { AutocompleteItem, ID, withDisplayAsNameAndDescription } from '@gh/common-shared-data';
import { FilteredDataQuery, getSlicedData, SlicedDataSource } from '@gh/core-data';
import { TranslationService } from '@gh/core-mls';
import { map$ } from '@gh/rx/operators';

import { uniqueId } from 'lodash';
import { Observable } from 'rxjs';

import { ComponentListCheckUsage } from '../../estimating/resources/component-list-item.model';
import {
  CSMasterCCInfo,
  CSMasterCheckUsage,
  cSMasterDetailsFromDto,
  cSMasterDetailsToDto,
  CSMasterHeadingInfo,
  CSMasterInfoDetails,
  CSMasterItemInfo,
  CSMasterListItem,
  CSMasterSubHeadingInfo,
  newCSMasterDetails,
  toClonedCSMasterDetails,
} from './cs-master.model';

@Injectable()
export class CSMasterBusinessService {
  constructor(private colorSelectionMasterRest: CsMasterRestService,
              private translations: TranslationService,
              private costCenterRest: CostCenterRestService,
              private jobRest: JobRestService,
              private reportService: ReportService) {
  }

  activate(item: CSMasterListItem): Observable<CSMasterListItem> {
    return this.colorSelectionMasterRest.undelete(item);
  }

  checkUsage(item: CSMasterListItem): Observable<CSMasterCheckUsage> {
    return this.colorSelectionMasterRest.checkUsage(item);
  }

  cloneFromCsMaster(id: ID): Observable<CSMasterInfoDetails> {
    return this.colorSelectionMasterRest.cloneFromCsMaster(id).pipe(
      map$(cSMasterDetailsFromDto),
      map$(toClonedCSMasterDetails));
  }

  cloneFromJobCs(id: ID): Observable<CSMasterInfoDetails> {
    return this.colorSelectionMasterRest.cloneFromJobCs(id).pipe(
      map$(cSMasterDetailsFromDto),
      map$(toClonedCSMasterDetails));
  }

  deactivateOrDelete(item: CSMasterListItem, checkUsage: ComponentListCheckUsage): Observable<any> {
    return this.colorSelectionMasterRest.deactivateOrDelete(item, checkUsage);
  }

  findCsMasterByName(query: FilteredDataQuery<string>): Observable<AutocompleteItem[]> {
    return this.colorSelectionMasterRest.findAllAsDataSource({ searchString: query.filter })
      .select(query).pipe(map$(getSlicedData));
  }

  findJobCsByName(query: FilteredDataQuery<string>): Observable<AutocompleteItem[]> {
    return this.jobRest.findAllAsDataSource({
        searchString: query.filter,
        isCsExist: true,
        showActive: true,
        showDpp: true,
        showFinalized: true,
        showNotProceeding: true,
      })
      .select(query).pipe(map$(getSlicedData), map$((jobs) =>
        jobs.map((job) => ({ id: job.id, name: `${job.jobNumber} ${job.contactName}` }))));
  }

  findAllAsDataSource(showDeleted: boolean): SlicedDataSource<CSMasterListItem> {
    return this.colorSelectionMasterRest.findAllAsDataSource({ showDeleted });
  }

  get(id: ID): Observable<CSMasterInfoDetails> {
    return this.colorSelectionMasterRest.get(id).pipe(map$(cSMasterDetailsFromDto));
  }

  initView(route: ActivatedRouteSnapshot): any {
    const itemId = getRouteParam(route, 'cSMasterDetailsId');
    const sourceCsMasterItemId = getRouteQueryParam(route, 'cloneFromCsMaster');
    const sourceJobCsItemId = getRouteQueryParam(route, 'cloneFromJobCs');

    return {
      details: isRouteForCreationMode(route)
        ? (sourceCsMasterItemId ? this.cloneFromCsMaster(sourceCsMasterItemId) :
          (sourceJobCsItemId ? this.cloneFromJobCs(sourceJobCsItemId) : newCSMasterDetails())) : this.get(itemId),
      reportUrl: this.reportService.generateUrl(REPORT_COLOR_SELECTION_MASTER, [
        reportIdField('Master', <ID>itemId),
      ]),
    };
  }

  loadCC(query: FilteredDataQuery<string>, excludeIds: ID[]): Observable<AutocompleteItem[]> {
    return this.costCenterRest
      .findAllAutocompleteItemsAsDataSource({
        searchString: query.filter,
        showDeleted: false,
        excludeIds,
      }).select(query).pipe(
        map$(getSlicedData),
        map$((items) => items.map(withDisplayAsNameAndDescription)));
  }

  save(details: CSMasterInfoDetails): Observable<CSMasterInfoDetails> {
    if (details.id) {
      return this.colorSelectionMasterRest.update(cSMasterDetailsToDto(details)).pipe(map$(cSMasterDetailsFromDto));
    } else {
      return this.colorSelectionMasterRest.create(cSMasterDetailsToDto(details)).pipe(map$(cSMasterDetailsFromDto));
    }
  }

  createNewHeading(): CSMasterHeadingInfo {
    return {
      id: uniqueId('heading:'),
      info: formStateSnapshot({
        name: this.translations.get('lbl_new_heading'),
        isNewPage: false,
      }),
      isNew: true,
      children: [],
    };
  }

  createNewSubHeading(): CSMasterSubHeadingInfo {
    return {
      id: uniqueId('subHeading:'),
      info: formStateSnapshot({
        name: this.translations.get('lbl_new_sub_heading'),
        text: '',
      }),
      children: [],
      isNew: true,
    };
  }

  createNewItem(): CSMasterItemInfo {
    return {
      id: uniqueId('item:'),
      info: formStateSnapshot({
        name: this.translations.get('lbl_new_item'),
        description: '',
      }),
      children: [],
      isNew: true,
    };
  }

  createNewCC(costCenter: AutocompleteItem): CSMasterCCInfo {
    return {
      id: costCenter.id,
      info: formStateSnapshot({ costCenter }),
    };
  }
}
